import { create } from 'zustand';
import { OtpCancelledError, OtpTooManyAttemptsError } from '@/lib/otp-errors';

type OtpStatus = 'idle' | 'open';
type OtpError = 'invalid' | null;

interface PendingResolver {
  resolve: (code: string) => void;
  reject: (err: Error) => void;
}

interface OtpStoreState {
  /** Whether an OTP form should be visible somewhere (modal or slot). */
  status: OtpStatus;
  /** Most recent submission is being verified — disables the form. */
  submitting: boolean;
  /** Error code surfaced on the form, drives copy. */
  error: OtpError;
  /** Number of currently-mounted `<OtpSlot>` components. When >0, the modal yields. */
  slotCount: number;

  /** Internal resolver tracking the in-flight requestOtp promise. */
  _resolver: PendingResolver | null;
  /** Internal cache of the in-flight promise so concurrent callers share it. */
  _promise: Promise<string> | null;

  /**
   * Open the form (or join the in-flight request) and wait for the user.
   * Returns a Promise that resolves with the entered OTP, or rejects with
   * an `OtpCancelledError` / `OtpTooManyAttemptsError`.
   */
  requestOtp: () => Promise<string>;

  /** Resolve the in-flight promise with the user-entered code. Status stays open, submitting=true. */
  submitOtp: (code: string) => void;

  /** Signal the previous submission was rejected by the backend as a bad code. Form stays open, error displayed, ready for retry. */
  markInvalid: () => void;

  /** User dismissed the form. Reject the in-flight promise with OtpCancelledError. */
  cancelOtp: () => void;

  /** Backend reported too many attempts. Reject the in-flight promise and close. */
  failTooManyAttempts: () => void;

  /** Close the form after a successful verification (interceptor calls this after retry success). */
  closeOtp: () => void;

  /** Increment the slot counter. Returns a deregister function (idempotent). */
  registerSlot: () => () => void;

  /** Test-only: reset all state. */
  __resetForTests: () => void;
}

const initialState = {
  status: 'idle' as OtpStatus,
  submitting: false,
  error: null as OtpError,
  slotCount: 0,
  _resolver: null as PendingResolver | null,
  _promise: null as Promise<string> | null,
};

export const useOtpStore = create<OtpStoreState>((set, get) => ({
  ...initialState,

  requestOtp: () => {
    const existing = get()._promise;
    if (existing) return existing;

    let resolver: PendingResolver | null = null;
    const promise = new Promise<string>((resolve, reject) => {
      resolver = { resolve, reject };
    });

    set({
      status: 'open',
      submitting: false,
      _resolver: resolver,
      _promise: promise,
    });
    return promise;
  },

  submitOtp: (code) => {
    const resolver = get()._resolver;
    set({ submitting: true, _resolver: null, _promise: null, error: null });
    resolver?.resolve(code);
  },

  markInvalid: () => {
    set({ submitting: false, error: 'invalid' });
  },

  cancelOtp: () => {
    const resolver = get()._resolver;
    set({ ...initialState, slotCount: get().slotCount });
    resolver?.reject(new OtpCancelledError());
  },

  failTooManyAttempts: () => {
    const resolver = get()._resolver;
    set({ ...initialState, slotCount: get().slotCount });
    resolver?.reject(new OtpTooManyAttemptsError());
  },

  closeOtp: () => {
    set({ ...initialState, slotCount: get().slotCount });
  },

  registerSlot: () => {
    set({ slotCount: get().slotCount + 1 });
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      set({ slotCount: Math.max(0, get().slotCount - 1) });
    };
  },

  __resetForTests: () => {
    set({ ...initialState });
  },
}));

/** Convenience selector hooks. */
export const useOtpStatus = () => useOtpStore((s) => s.status);
export const useOtpSubmitting = () => useOtpStore((s) => s.submitting);
export const useOtpError = () => useOtpStore((s) => s.error);
export const useOtpSlotCount = () => useOtpStore((s) => s.slotCount);
